/**
 * The shell's AppSwitcher (task 276, design r33 "The AppSwitcher"):
 * Identity's catalog through `visibleApps(catalog, bsp:apps).tiles`, the one
 * rule every BlueSphere header and Home use, so they cannot disagree
 * (Identity design r17 §F). As Ticketing's `useAppTiles` (its task 51).
 *
 * Display only: `bsp:apps` decides what to show, never what to allow; each
 * app checks its own access on its server. The catalog is optional. With no
 * `VITE_IDENTITY_CATALOG_URL`, or if `GET /catalog` fails, there are no
 * tiles and the shell draws no switcher, as Admin does. The console never
 * fails because the catalog is down.
 */

import { useQuery } from '@tanstack/react-query';

import { decodeBspApps, fetchCatalog, visibleApps, type Tile } from '@bluesphere/identity-client';

import { useAccessToken } from './useAccessToken';

/** Identity's `GET /catalog`, set by `scripts/deploy-web.sh`; empty means no AppSwitcher. */
export const CATALOG_URL: string = import.meta.env.VITE_IDENTITY_CATALOG_URL ?? '';

export function useAppTiles(catalogUrl: string = CATALOG_URL): Tile[] {
  const token = useAccessToken();

  const query = useQuery({
    queryKey: ['catalog', catalogUrl],
    queryFn: () => fetchCatalog(catalogUrl, token),
    enabled: Boolean(catalogUrl && token),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const catalog = query.data;
  if (!catalogUrl || !catalog || !Array.isArray(catalog.apps)) return [];
  return visibleApps(catalog, decodeBspApps(token)).tiles;
}
